/**
 * REST endpoint paths, relative to `VITE_API_BASE_URL` (which ends in `/api`).
 *
 * Port of `AppConstants` (rent_management_app/lib/core/constants.dart:12-60),
 * cross-checked path-by-path against what the Node backend actually mounts
 * (refactor/backend/src/app.ts:60-71 plus each module's routes.ts). The trailing
 * slashes are load-bearing — Django's URLconf and the Express port both declare
 * them, and Express does not redirect `/foo` to `/foo/`.
 *
 * Two of Flutter's constants are NOT reproduced here because they do not
 * resolve to a real endpoint on either backend; see the block comment at the
 * bottom of this file.
 */

// --- auth (Django: users.urls, mounted at /api/auth/) ----------------------
export const AUTH_LOGIN = 'auth/login/';
export const AUTH_REGISTER = 'auth/register/';
export const AUTH_REFRESH = 'auth/refresh/';
export const AUTH_LOGOUT = 'auth/logout/';
export const AUTH_ME = 'auth/me/';
export const AUTH_CHANGE_PASSWORD = 'auth/change-password/';
export const AUTH_NOTIFICATIONS = 'auth/notifications/';
/** User LIST only. PATCH/DELETE on `auth/{id}/` 404 on BOTH backends — see below. */
export const AUTH_USERS = 'auth/';

// --- properties ------------------------------------------------------------
export const PROPERTIES = 'properties/';
export const PROPERTIES_PUBLIC = 'properties/public/';
export const UNITS = 'properties/units/';
export const PROPERTY_IMAGES = 'properties/images/';

// --- tenants ---------------------------------------------------------------
export const LEASES = 'tenants/leases/';
export const TENANTS = 'tenants/tenants/';
/** Node-only feature, no Django/Flutter counterpart — see evictionNotices/routes.ts. */
export const EVICTION_NOTICES = 'tenants/eviction-notices/';

// --- payments --------------------------------------------------------------
export const PAYMENTS = 'payments/payments/';
export const INVOICES = 'payments/invoices/';
export const LEDGER = 'payments/ledger/';
export const PAYMENTS_UPCOMING = 'payments/upcoming/';
export const PAYMENTS_OVERDUE = 'payments/overdue/';
/** Node-only feature, no Flutter/AppConstants counterpart — see recurringCharges/routes.ts. */
export const RECURRING_CHARGES = 'payments/recurring-charges/';

// --- maintenance -----------------------------------------------------------
export const MAINTENANCE = 'maintenance/';
export const MAINTENANCE_IMAGES = 'maintenance/images/';

// --- expenses --------------------------------------------------------------
export const EXPENSES = 'expenses/';
export const EXPENSES_SUMMARY = 'expenses/summary/';

// --- reports ---------------------------------------------------------------
export const REPORTS_DASHBOARD = 'reports/dashboard/';
export const REPORTS_RENT_ROLL = 'reports/rent-roll/';
export const REPORTS_FINANCIAL = 'reports/financial/';
/** Served by the Node backend but absent from Flutter's AppConstants. */
export const REPORTS_MAINTENANCE = 'reports/maintenance/';
/** Node-only, no Django counterpart — see reports/routes.ts's `GET tax-summary/`. */
export const REPORTS_TAX_SUMMARY = 'reports/tax-summary/';

// --- m-pesa ----------------------------------------------------------------
export const MPESA_STK_PUSH = 'mpesa/stk-push/';
export const MPESA_STK_QUERY = 'mpesa/stk-query/';
export const MPESA_QR = 'mpesa/qr/';
/** Called by Safaricom, never by this app. Listed for completeness only. */
export const MPESA_CALLBACK = 'mpesa/callback/';

// --- offboarding -----------------------------------------------------------
export const OFFBOARDING_REQUESTS = 'offboarding/requests/';
export const OFFBOARDING_INSPECTIONS = 'offboarding/inspections/';
export const OFFBOARDING_SETTLEMENTS = 'offboarding/settlements/';

// --- messaging -------------------------------------------------------------
export const MESSAGING_CONVERSATIONS = 'messaging/conversations/';
export const MESSAGING_MESSAGES = 'messaging/messages/';
export const MESSAGING_BROADCASTS = 'messaging/broadcasts/';

// --- push (FCM web) ----------------------------------------------------------
/** Node-only, no Flutter/AppConstants counterpart — see push/routes.ts. */
export const PUSH_TOKEN = 'push/token/';

/**
 * ---------------------------------------------------------------------------
 * DELIBERATE DIVERGENCES FROM `AppConstants` — do not "restore" these.
 * ---------------------------------------------------------------------------
 *
 * 1. `AppConstants.payments = 'payments/'` (constants.dart:33) is WRONG against
 *    both backends. Django registers `PaymentViewSet` on a `DefaultRouter` at
 *    `payments/` (rent_management/payments/urls.py:8-11), so the payment LIST is
 *    `/api/payments/payments/`; the bare `/api/payments/` is the router's
 *    browsable API-root view, which returns a dict of links, not a list. The
 *    Node backend matches Django exactly (only `/invoices/`, `/ledger/`,
 *    `/payments/`, `/upcoming/`, `/overdue/` exist under `/api/payments`), so
 *    `payment_provider.dart:49` is a pre-existing Flutter bug, not a port gap.
 *    `PAYMENTS` above is the corrected path.
 *
 * 2. `payment_provider.dart:153` calls `payments/{id}/record_payment/` and
 *    `:319` calls `payments/bulk_create_utility_charges/`. NEITHER exists in
 *    Django: `PaymentViewSet`'s detail action is `record_actual_payment`
 *    (rent_management/payments/views.py:241) and there is no bulk-utility action
 *    at all. Both calls 404 against Django today and 404 against Node. The
 *    correct action is `${PAYMENTS}{id}/record_actual_payment/`; there is no
 *    correct path for the bulk one — it needs building on both backends before a
 *    UI can use it.
 *
 * 3. `PATCH`/`DELETE auth/{id}/` (auth_provider.dart:408,437) are routed by
 *    neither backend — already documented in refactor/docs/PARITY_NOTES.md under
 *    "`PATCH`/`DELETE /api/auth/{id}/` are 404 in both backends". The user
 *    management page will need those endpoints added to BOTH backends
 *    deliberately; do not assume they exist.
 */
